import React, { useEffect, useState, useCallback } from 'react';
import { X, MapPin, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'velmora_enquiry_popup_dismissed';
const MOBILE_FALLBACK_MS = 30000; // 30s fallback timer for mobile (no exit-intent)

/**
 * Exit-intent Trip Enquiry Popup.
 *
 * Trigger rules:
 *  - Desktop (pointer:fine): mouse leaves top of the viewport (exit-intent).
 *  - Mobile / touch: shown after 30s of session activity.
 *  - Only shows once per session (sessionStorage-based dismissal).
 *
 * On submit -> POST /api/contact so leads land in the existing CMS dashboard
 * with the source flag "Exit-intent popup" prepended to the message.
 */
export const TripEnquiryPopup = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    travelDate: '',
    travelers: '',
  });

  const alreadyShown = useCallback(
    () => sessionStorage.getItem(STORAGE_KEY) === '1',
    []
  );

  const markShown = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (_) {
      /* private mode */
    }
  }, []);

  const trigger = useCallback(() => {
    if (alreadyShown()) return;
    setOpen(true);
    markShown();
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'popup_shown', {
        event_category: 'engagement',
        event_label: 'trip_enquiry_exit_intent',
      });
    }
  }, [alreadyShown, markShown]);

  useEffect(() => {
    if (alreadyShown()) return;

    const isTouch =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches;

    if (isTouch) {
      const t = setTimeout(trigger, MOBILE_FALLBACK_MS);
      return () => clearTimeout(t);
    }

    // Desktop: exit intent (mouse leaves via top edge)
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => document.removeEventListener('mouseleave', onMouseLeave);
  }, [alreadyShown, trigger]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      setError('Please fill in your name, phone and email.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        travelers: form.travelers || undefined,
        travelDate: form.travelDate || undefined,
        message:
          '[Exit-intent popup] Requesting a FREE custom Kashmir itinerary.',
      };
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
      setSuccess(true);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'lead',
          event_label: 'exit_intent_popup',
          method: 'popup',
          value: 1,
        });
      }
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enquiry-popup-title"
      data-testid="trip-enquiry-popup"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        {/* Header banner */}
        <div className="relative bg-gradient-to-br from-amber-700 via-amber-800 to-orange-900 px-6 py-6 text-white">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 rounded-full p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
            aria-label="Close popup"
            data-testid="enquiry-popup-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5" />
            <span className="text-xs uppercase tracking-widest font-semibold text-amber-100">
              Wait — before you leave
            </span>
          </div>
          <h2
            id="enquiry-popup-title"
            className="text-2xl sm:text-3xl font-bold leading-tight"
          >
            Get a Free Custom Kashmir Itinerary
            <span className="block text-amber-200 text-base font-medium mt-1">
              Delivered to your inbox within 24 hours
            </span>
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {success ? (
            <div
              className="py-6 text-center space-y-3"
              data-testid="enquiry-popup-success"
            >
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
              <h3 className="text-xl font-bold text-slate-800">
                You&apos;re all set!
              </h3>
              <p className="text-slate-600 text-sm">
                Our Kashmir travel expert will send your free itinerary within
                24 hours. Meanwhile, feel free to browse our packages.
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-2 px-6 py-2 rounded-full bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 transition-colors"
                data-testid="enquiry-popup-done"
              >
                Continue browsing
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  required
                  placeholder="Your Name*"
                  value={form.name}
                  onChange={handleChange('name')}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  data-testid="enquiry-name-input"
                />
                <input
                  type="tel"
                  required
                  placeholder="Phone Number*"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  data-testid="enquiry-phone-input"
                />
              </div>
              <input
                type="email"
                required
                placeholder="Email Address*"
                value={form.email}
                onChange={handleChange('email')}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                data-testid="enquiry-email-input"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="date"
                  placeholder="Travel Date"
                  value={form.travelDate}
                  onChange={handleChange('travelDate')}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent text-slate-600"
                  data-testid="enquiry-date-input"
                />
                <select
                  value={form.travelers}
                  onChange={handleChange('travelers')}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent text-slate-600"
                  data-testid="enquiry-travelers-input"
                >
                  <option value="">Group size</option>
                  <option value="1">Solo</option>
                  <option value="2">Couple (2)</option>
                  <option value="3-4">Family (3-4)</option>
                  <option value="5+">Group (5+)</option>
                </select>
              </div>

              {error && (
                <p
                  className="text-sm text-red-600"
                  data-testid="enquiry-popup-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-lg bg-amber-700 hover:bg-amber-800 text-white font-semibold text-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="enquiry-popup-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send My Free Itinerary'
                )}
              </button>
              <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                No spam — used only to send your custom Kashmir itinerary.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripEnquiryPopup;
